/**
 * Arrows and rings drawn on a board, lichess-style.
 *
 * Pure helpers, no React: useBoardArrows turns pointer events into calls to
 * these. A shape is square-based, never pixel-based, so a drawing survives the
 * board being resized or flipped.
 */

/** One drawn mark. `from === to` is a ring around that square. */
export type Shape = {from: string; to: string; colour: string};

/** The lichess brushes. */
export const ARROW_COLOURS = {
  green: '#15781B',
  red: '#882020',
  blue: '#003088',
  yellow: '#e68f00',
};

/**
 * The colour for the modifier keys held while drawing.
 *
 * Shift is red and alt (option) is blue, as on lichess; ctrl or cmd is yellow,
 * and so is shift+alt, which is how lichess users already reach yellow.
 */
export function colourFor(mods: {
  shift?: boolean;
  alt?: boolean;
  ctrl?: boolean;
  meta?: boolean;
}): string {
  if (mods.ctrl || mods.meta || (mods.shift && mods.alt)) return ARROW_COLOURS.yellow;
  if (mods.shift) return ARROW_COLOURS.red;
  if (mods.alt) return ARROW_COLOURS.blue;
  return ARROW_COLOURS.green;
}

/**
 * The square under a point, in pixels from the board's top-left corner.
 *
 * The inverse of squareOffset: from white's side a8 is top-left and a1
 * bottom-left, and flipping the board mirrors both axes. A square owns its top
 * and left edges, so the right and bottom edges of the board are already off it.
 */
export function squareAt(
  x: number,
  y: number,
  boardSize: number,
  orientation: 'white' | 'black',
): string | null {
  if (!Number.isFinite(boardSize) || boardSize <= 0) return null;
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  if (x < 0 || y < 0 || x >= boardSize || y >= boardSize) return null;

  const size = boardSize / 8;
  // min() guards against x/size rounding up to 8 a hair inside the far edge.
  const col = Math.min(7, Math.floor(x / size));
  const rowFromTop = Math.min(7, Math.floor(y / size));

  const file = orientation === 'white' ? col : 7 - col; // a=0 … h=7
  const rank = orientation === 'white' ? 8 - rowFromTop : rowFromTop + 1;
  return `${String.fromCharCode(97 + file)}${rank}`;
}

/**
 * Draw `next` on top of `shapes`, the way lichess does: drawing a shape that is
 * already there in the same colour rubs it out, in another colour recolours it
 * (in place, so the drawing order does not jump), and anything new is added.
 */
export function toggleShape(shapes: Shape[], next: Shape): Shape[] {
  const i = shapes.findIndex(s => s.from === next.from && s.to === next.to);
  if (i < 0) return [...shapes, next];
  if (shapes[i].colour === next.colour) return shapes.filter((_, j) => j !== i);
  return shapes.map((s, j) => (j === i ? next : s));
}

/**
 * The arrows in react-chessboard's `customArrows` format. Rings are left out:
 * react-chessboard drops any arrow that starts and ends on the same square.
 */
export function boardArrows(shapes: Shape[]): [string, string, string][] {
  return shapes.filter(s => s.from !== s.to).map(s => [s.from, s.to, s.colour]);
}

/** The rings, which the board cannot draw itself and need an overlay. */
export function boardRings(shapes: Shape[]): {square: string; colour: string}[] {
  return shapes.filter(s => s.from === s.to).map(s => ({square: s.from, colour: s.colour}));
}
